import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { adminApi, type ApplicationStatus } from '../lib/api';
import { formatDate } from '../lib/format';

const PAGE_SIZE = 50;

const STATUS_OPTIONS: { value: ApplicationStatus; label: string }[] = [
  { value: 'new', label: '신규' },
  { value: 'contacted', label: '연락함' },
  { value: 'invited', label: '초대함' },
  { value: 'declined', label: '거절' },
];

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  new: '신규',
  contacted: '연락함',
  invited: '초대함',
  declined: '거절',
};

export function Applications() {
  const [status, setStatus] = useState<ApplicationStatus | ''>('');
  const [page, setPage] = useState(0);
  const qc = useQueryClient();

  const offset = page * PAGE_SIZE;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'applications', status, offset],
    queryFn: () =>
      adminApi.listApplications({ status: status || undefined, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: ({ id, next }: { id: string; next: ApplicationStatus }) =>
      adminApi.updateApplication(id, { status: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'applications'] }),
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">이용 신청</h1>
          <p className="text-sm text-text-muted mt-1">
            {isLoading ? '로딩 중…' : `${total}건`}
          </p>
        </div>
        <select
          className="input max-w-[10rem]"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ApplicationStatus | '');
            setPage(0);
          }}
        >
          <option value="">전체 상태</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </header>

      {isError && (
        <div className="card text-sm text-error">
          불러오기 실패: {(error as { error?: string })?.error ?? String(error)}
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-elev text-text-secondary">
            <tr>
              <Th>이름</Th>
              <Th>회사</Th>
              <Th>연락처</Th>
              <Th>메시지</Th>
              <Th>신청일</Th>
              <Th>상태</Th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                  신청 없음
                </td>
              </tr>
            )}
            {data?.items.map((a) => (
              <tr key={a.id} className="border-t border-border hover:bg-surface-elev align-top">
                <Td className="font-medium text-text-primary">
                  {a.name}
                  {a.companyRole ? (
                    <span className="block text-xs text-text-muted">{a.companyRole}</span>
                  ) : null}
                </Td>
                <Td className="text-text-secondary">{a.companyName ?? '—'}</Td>
                <Td className="text-text-secondary text-xs">
                  <div className="font-mono">{a.phone}</div>
                  {a.email ? <div className="text-text-muted">{a.email}</div> : null}
                </Td>
                <Td className="text-text-secondary max-w-xs">
                  {a.message ? (
                    <span className="whitespace-pre-wrap break-words">{a.message}</span>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </Td>
                <Td className="text-text-muted text-xs whitespace-nowrap">{formatDate(a.createdAt)}</Td>
                <Td>
                  <select
                    className="input py-1 text-xs"
                    value={a.status}
                    disabled={mutation.isPending}
                    onChange={(e) =>
                      mutation.mutate({ id: a.id, next: e.target.value as ApplicationStatus })
                    }
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {STATUS_LABEL[s.value]}
                      </option>
                    ))}
                  </select>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-text-muted">
        <div>
          {total === 0 ? 0 : offset + 1} – {Math.min(offset + PAGE_SIZE, total)} / {total}
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            이전
          </button>
          <button
            className="btn-ghost"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide ${className ?? ''}`}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 align-top ${className ?? ''}`}>{children}</td>;
}
